import React, { useEffect, useState } from 'react';
import { Cake, Loader, Check } from 'lucide-react';
import patientService from '../../services/patientService';
import { getTodayInBogota } from '../../utils/appointmentSchedule';
import { debugError } from '../../utils/logger';

// Editor de la fecha de nacimiento en la pestaña "Perfil". La edad sale en la
// historia clínica y en las órdenes, así que el paciente debe poder corregirla.
//
// La fecha se guarda como medianoche UTC del día: se lee con slice(0, 10) y no
// con toLocaleDateString, que en Bogotá la mostraría un día antes.
// onSaved recibe el perfil actualizado, para que quien lo monta no vuelva a
// pedir la fecha al agendar.
function BirthDateEditor({ onSaved }) {
  const [patientId, setPatientId] = useState(null);
  const [birthDate, setBirthDate] = useState('');
  const [hasSaved, setHasSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await patientService.getMyProfile();
        if (mounted) {
          setPatientId(res?.data?._id || null);
          const stored = res?.data?.dateOfBirth;
          setBirthDate(stored ? String(stored).slice(0, 10) : '');
          setHasSaved(Boolean(stored));
        }
      } catch (err) {
        debugError('Error cargando fecha de nacimiento:', err);
        if (mounted) setError('No se pudo cargar tu fecha de nacimiento');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const handleSave = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(birthDate) || birthDate > getTodayInBogota()) {
      setError('Ingresa una fecha de nacimiento válida');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await patientService.updateProfile(patientId, { dateOfBirth: birthDate });
      setHasSaved(true);
      if (onSaved && res?.data) onSaved(res.data);
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
    } catch (err) {
      debugError('Error actualizando fecha de nacimiento:', err);
      setError(err?.response?.data?.message || 'No se pudo actualizar tu fecha de nacimiento.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-xl">
      <div className="flex items-center gap-3 mb-4">
        <Cake className="h-6 w-6 text-primary-600" />
        <h3 className="text-lg font-bold text-gray-800">Fecha de nacimiento</h3>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500">
          <Loader className="h-4 w-4 animate-spin" /> Cargando...
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-3">
            {hasSaved
              ? 'El médico la usa para calcular tu edad en la historia clínica.'
              : 'Aún no has registrado tu fecha de nacimiento. El médico la necesita para tu historia clínica.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="date"
              aria-label="Fecha de nacimiento"
              value={birthDate}
              max={getTodayInBogota()}
              min="1900-01-01"
              onChange={(e) => setBirthDate(e.target.value)}
              disabled={saving || !patientId}
              className="input-field flex-1"
            />
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !patientId}
              className="btn-primary flex items-center justify-center"
            >
              {saving ? <Loader className="h-4 w-4 animate-spin" /> : 'Guardar'}
            </button>
          </div>

          <div className="mt-3 min-h-[20px] text-sm">
            {!saving && savedAt && (
              <span className="text-green-600 inline-flex items-center gap-1">
                <Check className="h-4 w-4" /> Fecha guardada
              </span>
            )}
            {error && <span className="text-red-600">{error}</span>}
          </div>
        </>
      )}
    </div>
  );
}

export default BirthDateEditor;
